import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API } from '../App';
import axios from 'axios';
import Navbar from '../components/Navbar';
import CitySelect from '../components/CitySelect';
import { ArrowLeft, Mail, Phone, MapPin, Clock, Send, MessageCircle, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const ContactUsPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    city: '',
    subject: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      toast.error('Please fill all required fields');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitted(true);
      toast.success('Message sent! We\'ll get back to you soon.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send message');
    } finally {
      setSubmitting(false);
    }
  };

  const subjects = [
    'General Inquiry',
    'Loan Application Support',
    'Credit Score Help',
    'Technical Issue',
    'Partnership Opportunity',
    'Complaint or Feedback',
    'Other'
  ];

  return (
    <div style={{ backgroundColor: '#FFFFFF', minHeight: '100vh' }}>
      <Navbar />

      {/* Hero Banner */}
      <section style={{ background: 'linear-gradient(135deg, #FFF7ED 0%, #FFFFFF 100%)', padding: '5rem 2rem 3rem' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <button 
            onClick={() => navigate('/')}
            style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: 'none', border: 'none', color: '#FF6B35', cursor: 'pointer', marginBottom: '2rem', fontWeight: '600' }}
            data-testid="back-to-home-button"
          >
            <ArrowLeft size={20} /> Back to Home
          </button>

          <div style={{ display: 'inline-block', padding: '0.4rem 1rem', backgroundColor: '#FF6B35', borderRadius: '9999px', color: 'white', fontWeight: '700', fontSize: '0.8rem', marginBottom: '1.5rem', letterSpacing: '0.05em' }}>
            GET IN TOUCH
          </div>
          <h1 style={{ fontSize: 'clamp(2rem, 5vw, 3.5rem)', marginBottom: '1rem', color: '#0F172A', fontWeight: '800', lineHeight: 1.1 }}>
            We're Here to <span className="gradient-text-primary">Help</span>
          </h1>
          <p style={{ fontSize: '1.125rem', color: '#475569', maxWidth: '600px', lineHeight: 1.7 }}>
            Got questions about loans, banking services, or your application? Our team is ready to assist you 24/7.
          </p>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section style={{ padding: '3rem 2rem' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '1.5rem', marginBottom: '4rem' }}>
            <div className="stat-card" style={{ textAlign: 'center', padding: '2rem 1.5rem' }} data-testid="contact-info-phone">
              <div style={{ width: '64px', height: '64px', borderRadius: '16px', background: 'linear-gradient(135deg, #FF6B35 0%, #FFB627 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1rem' }}>
                <Phone size={28} style={{ color: 'white' }} />
              </div>
              <h3 style={{ fontSize: '1.125rem', color: '#0F172A', marginBottom: '0.5rem' }}>Call / WhatsApp</h3>
              <a href="tel:+917054986771" style={{ color: '#FF6B35', fontWeight: '700', fontSize: '1.1rem', textDecoration: 'none' }} data-testid="contact-info-phone-number">+91 70549 86771</a>
              <p style={{ color: '#94A3B8', fontSize: '0.85rem', marginTop: '0.5rem' }}>Tax, Loans & Financial Services • Mon-Sat, 9 AM - 8 PM</p>
            </div>

            <div className="stat-card" style={{ textAlign: 'center', padding: '2rem 1.5rem' }} data-testid="contact-info-email">
              <div style={{ width: '64px', height: '64px', borderRadius: '16px', background: 'linear-gradient(135deg, #004D7A 0%, #0077B6 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1rem' }}>
                <Mail size={28} style={{ color: 'white' }} />
              </div>
              <h3 style={{ fontSize: '1.125rem', color: '#0F172A', marginBottom: '0.5rem' }}>Email Us</h3>
              <a href="mailto:support@myarthik.com" style={{ color: '#0077B6', fontWeight: '700', fontSize: '1.05rem', textDecoration: 'none' }}>support@myarthik.com</a>
              <p style={{ color: '#94A3B8', fontSize: '0.85rem', marginTop: '0.5rem' }}>Response within 24 hours</p>
            </div>

            <div className="stat-card" style={{ textAlign: 'center', padding: '2rem 1.5rem' }} data-testid="contact-info-office">
              <div style={{ width: '64px', height: '64px', borderRadius: '16px', background: 'linear-gradient(135deg, #FFB627 0%, #FFCC00 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1rem' }}>
                <MapPin size={28} style={{ color: 'white' }} />
              </div>
              <h3 style={{ fontSize: '1.125rem', color: '#0F172A', marginBottom: '0.5rem' }}>Visit Us</h3>
              <p style={{ color: '#475569', fontWeight: '600', fontSize: '0.95rem', lineHeight: 1.5 }}>
                Arthik Finance Tower<br />Bandra Kurla Complex<br />Mumbai - 400051
              </p>
            </div>

            <div className="stat-card" style={{ textAlign: 'center', padding: '2rem 1.5rem' }} data-testid="contact-info-hours">
              <div style={{ width: '64px', height: '64px', borderRadius: '16px', background: 'linear-gradient(135deg, #16A34A 0%, #22C55E 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1rem' }}>
                <Clock size={28} style={{ color: 'white' }} />
              </div>
              <h3 style={{ fontSize: '1.125rem', color: '#0F172A', marginBottom: '0.5rem' }}>Business Hours</h3>
              <p style={{ color: '#475569', fontWeight: '600', fontSize: '0.95rem', lineHeight: 1.5 }}>
                Mon-Fri: 9 AM - 8 PM<br />Sat: 10 AM - 6 PM<br />Sun: Closed
              </p>
            </div>
          </div>

          {/* Contact Form & FAQ */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '2rem' }}>
            {/* Form */}
            <div className="calculator-section" data-testid="contact-form-section">
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                <MessageCircle size={28} style={{ color: '#FF6B35' }} />
                <h2 style={{ fontSize: '1.75rem', color: '#0F172A', margin: 0 }}>Send Us a Message</h2>
              </div>
              <p style={{ color: '#475569', marginBottom: '2rem' }}>Fill the form below and we'll respond shortly</p>

              {submitted ? (
                <div style={{ textAlign: 'center', padding: '3rem 1rem' }} data-testid="contact-success-message">
                  <div style={{ width: '80px', height: '80px', borderRadius: '50%', background: 'linear-gradient(135deg, #16A34A 0%, #22C55E 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem' }}>
                    <CheckCircle size={40} style={{ color: 'white' }} />
                  </div>
                  <h3 style={{ fontSize: '1.5rem', color: '#0F172A', marginBottom: '0.75rem' }}>Message Sent!</h3>
                  <p style={{ color: '#475569', marginBottom: '2rem' }}>
                    Thank you for reaching out. We've received your message and will get back to you within 24 hours.
                  </p>
                  <button 
                    onClick={() => { setSubmitted(false); setFormData({ name: '', email: '', phone: '', subject: '', message: '' }); }}
                    className="primary-button"
                    data-testid="send-another-message-button"
                  >
                    Send Another Message
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit}>
                  <div style={{ display: 'grid', gap: '1.25rem' }}>
                    <div>
                      <label className="label-text">Full Name *</label>
                      <input
                        type="text"
                        className="input-field"
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        placeholder="John Doe"
                        required
                        data-testid="contact-name-input"
                      />
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem' }}>
                      <div>
                        <label className="label-text">Email *</label>
                        <input
                          type="email"
                          className="input-field"
                          value={formData.email}
                          onChange={(e) => setFormData({...formData, email: e.target.value})}
                          placeholder="you@example.com"
                          required
                          data-testid="contact-email-input"
                        />
                      </div>
                      <div>
                        <label className="label-text">Phone</label>
                        <input
                          type="tel"
                          className="input-field"
                          value={formData.phone}
                          onChange={(e) => setFormData({...formData, phone: e.target.value})}
                          placeholder="+91 XXXXX XXXXX"
                          data-testid="contact-phone-input"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="label-text">City</label>
                      <CitySelect
                        value={formData.city}
                        onChange={(v) => setFormData({ ...formData, city: v })}
                        testId="contact-city-input"
                      />
                    </div>

                    <div>
                      <label className="label-text">Subject *</label>
                      <select
                        className="input-field"
                        value={formData.subject}
                        onChange={(e) => setFormData({...formData, subject: e.target.value})}
                        required
                        data-testid="contact-subject-select"
                      >
                        <option value="">-- Select a subject --</option>
                        {subjects.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>

                    <div>
                      <label className="label-text">Your Message *</label>
                      <textarea
                        className="input-field"
                        value={formData.message}
                        onChange={(e) => setFormData({...formData, message: e.target.value})}
                        placeholder="Tell us how we can help..."
                        rows={5}
                        required
                        data-testid="contact-message-input"
                      />
                    </div>

                    <button
                      type="submit"
                      className="primary-button"
                      disabled={submitting}
                      style={{ justifyContent: 'center', padding: '1rem' }}
                      data-testid="contact-submit-button"
                    >
                      {submitting ? 'Sending...' : <>Send Message <Send size={18} /></>}
                    </button>
                  </div>
                </form>
              )}
            </div>

            {/* FAQ Sidebar */}
            <div>
              <div className="calculator-section" style={{ marginBottom: '1.5rem' }}>
                <h3 style={{ fontSize: '1.5rem', marginBottom: '1.5rem', color: '#0F172A' }}>Quick Answers</h3>
                
                {[
                  { q: 'How fast can I get a loan approved?', a: 'Most applications get a preliminary response within 3-5 working days.' },
                  { q: 'Is there any charge for checking eligibility?', a: 'No! All our calculators and credit score checks are 100% free.' },
                  { q: 'Are my documents safe?', a: 'Yes. We use bank-grade encryption and never share data with unauthorized parties.' },
                  { q: 'Can I apply for multiple loans simultaneously?', a: 'Yes, but multiple credit inquiries may affect your credit score.' }
                ].map((faq, idx) => (
                  <div key={idx} style={{ paddingBottom: '1rem', marginBottom: '1rem', borderBottom: idx < 3 ? '1px solid #E2E8F0' : 'none' }}>
                    <h4 style={{ fontSize: '0.95rem', color: '#0F172A', marginBottom: '0.5rem', fontWeight: '700' }}>
                      {faq.q}
                    </h4>
                    <p style={{ fontSize: '0.875rem', color: '#475569', lineHeight: 1.6 }}>
                      {faq.a}
                    </p>
                  </div>
                ))}
              </div>

              <div style={{ padding: '2rem', background: 'linear-gradient(135deg, #FF6B35 0%, #E55100 100%)', borderRadius: '1rem', color: 'white', textAlign: 'center' }}>
                <h3 style={{ fontSize: '1.25rem', marginBottom: '0.75rem' }}>Need Immediate Help?</h3>
                <p style={{ fontSize: '0.95rem', opacity: 0.95, marginBottom: '1.5rem' }}>
                  Our support team is just a call away
                </p>
                <a 
                  href="tel:+917054986771"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', background: 'white', color: '#FF6B35', padding: '0.75rem 1.5rem', borderRadius: '0.5rem', fontWeight: '700', textDecoration: 'none' }}
                  data-testid="call-now-button"
                >
                  <Phone size={18} /> Call +91 70549 86771
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactUsPage;
